import { useRef, useCallback, useLayoutEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Checkbox } from "~/components/Checkbox";
import { DatadogLinks } from "~/components/DatadogDashboardLink";
import { GitInfo } from "~/components/GitInfo";
import { Icon } from "~/components/icon";
import { SettingsOverridesSummary } from "~/components/SettingsOverrides";
import { isByocCluster, type Cluster } from "~/types";
import { cn } from "~/utils";

import { CommitsBehindBadge } from "./CommitsBehindBadge";
import { TimeSinceUpdate } from "./TimeSinceUpdate";

interface ClusterItemProps {
  cluster: Cluster;
  className: string;
  onHover?: () => void;
  onHoverEnd?: () => void;
  onFocus?: () => void;
  onToggleSelection: () => void;
  onShiftClick?: () => void;
  isSelected: boolean;
  isHighlighted: boolean;
  hasMultipleSelections: boolean;
}

export function ClusterItem({
  cluster,
  className,
  onHover,
  onHoverEnd,
  onFocus,
  onToggleSelection,
  onShiftClick,
  isSelected,
  isHighlighted,
  hasMultipleSelections,
}: ClusterItemProps) {
  const mouseStartPos = useRef<{ x: number; y: number } | null>(null);
  const lastThrottleTime = useRef<number>(0);
  const throttleMs = 50;
  const [isHovered, setIsHovered] = useState(false);

  const isByoc = isByocCluster(cluster);

  const handleClick = (e: React.MouseEvent) => {
    // Don't handle click if it came from the checkbox
    if ((e.target as HTMLElement).tagName === "INPUT") {
      return;
    }

    if (hasMultipleSelections) {
      e.preventDefault();
      onToggleSelection();
    } else if (e.shiftKey) {
      e.preventDefault();
      onShiftClick?.();
    } else {
      sessionStorage.setItem("clusterListScrollPos", window.scrollY.toString());
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checkbox = e.currentTarget;
    e.stopPropagation();
    onToggleSelection();

    // Blur the checkbox to ensure keybindings work after selection
    // Use setTimeout to avoid blocking the click event
    setTimeout(() => checkbox.blur(), 0);
  };

  // This is to prevent the hover state from being triggered while the mouse is over
  // the cluster item and the user is navigating with the keyboard.
  //
  // We store the mouse position when the user starts navigating with the keyboard,
  // and then check if the mouse has moved more than 16 pixels (picked arbitrarily, based on vibes)
  // from that position. If it has, we clear the threshold and allow hover.
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const now = Date.now();
      if (now - lastThrottleTime.current < throttleMs) {
        return;
      }
      lastThrottleTime.current = now;

      if (!mouseStartPos.current) {
        mouseStartPos.current = { x: e.clientX, y: e.clientY };
        return;
      }

      const deltaX = Math.abs(e.clientX - mouseStartPos.current.x);
      const deltaY = Math.abs(e.clientY - mouseStartPos.current.y);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      if (distance > 16) {
        mouseStartPos.current = null;
        onHover?.();
      }
    },
    [onHover],
  );

  // always trigger when mouse first enters the element — the above
  // threshold only applies to movement within the cluster list item
  const handleMouseEnter = useCallback(() => {
    // Reset mouse tracking when entering a new element
    mouseStartPos.current = null;
    setIsHovered(true);
    onHover?.();
  }, [onHover]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onHoverEnd?.();
  }, [onHoverEnd]);

  // Show checkbox when hovering OR when there are multiple selections
  const shouldShowCheckbox = isHovered || hasMultipleSelections;

  return (
    <Link
      to={hasMultipleSelections ? "#" : `/clusters/${cluster.id}`}
      data-cluster-id={cluster.id}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={onFocus}
      tabIndex={0}
      className={cn(
        "relative block rounded-sm border border-gray-200 bg-white p-3 transition-all focus:outline-none md:p-2",
        className,
      )}
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-2">
        {/* Cluster ID and Lock */}
        <div className="flex min-w-0 flex-1 items-center gap-1 md:min-w-40">
          <div className="flex h-4 w-6 flex-shrink-0 items-center justify-center">
            {shouldShowCheckbox ? (
              <Checkbox
                checked={isSelected}
                onClick={(e) => e.stopPropagation()}
                onChange={(_checked, event) => handleCheckboxChange(event)}
                ariaLabel={`${isSelected ? "Deselect" : "Select"} ${cluster.id}`}
              />
            ) : null}
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-visible">
            <ExpandableClusterName name={cluster.id} expanded={isHighlighted} />
            {cluster.lock_reason != null ? (
              <div
                title={
                  cluster.lock_reason
                    ? `Locked: ${cluster.lock_reason}`
                    : "Cluster is locked"
                }
              >
                <Icon name="Lock" size={16} className="text-orange-500" />
              </div>
            ) : null}
          </div>
        </div>

        {/* Commits Behind Badge - Mobile positioning */}
        {cluster.git_details?.behind_by != null && isSelected ? (
          <div className="md:hidden">
            <CommitsBehindBadge
              behindBy={cluster.git_details.behind_by}
              isByoc={isByoc}
              currentCommitDate={cluster.git_details?.current_commit?.date}
              className="transition-opacity"
            />
          </div>
        ) : null}

        {/* Info Grid */}
        <div className="relative grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3 md:w-auto md:flex-none md:grid-cols-[7rem_auto_4.5rem_3rem_2.5rem_2rem] md:items-center md:gap-3">
          {/* Commits Behind - Desktop positioning */}
          <div className="hidden items-center justify-start md:flex">
            {cluster.git_details?.behind_by != null ? (
              <CommitsBehindBadge
                behindBy={cluster.git_details.behind_by}
                isByoc={isByoc}
                currentCommitDate={cluster.git_details?.current_commit?.date}
                className={cn("transition-opacity", !isSelected && "opacity-0")}
              />
            ) : null}
          </div>

          {/* Git Details */}
          <div className="flex min-w-0 items-center gap-2">
            <span className="text-xs text-gray-500 md:hidden">Version:</span>
            <GitInfo
              gitDetails={cluster.git_details}
              showDetails={false}
              isByoc={isByoc}
              className="w-fit"
            />
          </div>

          {/* Time Since Update */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 md:hidden">Updated:</span>
            <TimeSinceUpdate updatedAt={cluster.updated_at} />
          </div>

          {/* Node Count */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 md:hidden">Nodes:</span>
            <ClusterSize cluster={cluster} />
          </div>

          {/* Settings Overrides */}
          <div className="flex items-center gap-2 sm:absolute sm:right-0 sm:top-1/2 sm:-translate-y-1/2 md:static md:translate-y-0">
            <span className="text-xs text-gray-500 sm:hidden">Overrides:</span>
            <SettingsOverridesSummary
              settingsOverrides={cluster.settings_overrides}
            />
          </div>

          {/* Datadog Links */}
          <div className="hidden md:block" onClick={(e) => e.stopPropagation()}>
            <DatadogLinks clusters={cluster} buttonStyle="subtle" />
          </div>
        </div>
      </div>
    </Link>
  );
}

/** Truncates long names, expands on hover or when highlighted via keyboard */
function ExpandableClusterName({
  name,
  expanded,
}: {
  name: string;
  expanded: boolean;
}) {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [isLongName, setIsLongName] = useState(false);
  const showExpanded = isLongName && expanded;

  // How much room a name gets depends on the window, not its length.
  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el) return;
    const measure = () => setIsLongName(el.scrollWidth > el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [name]);

  return (
    <div className="group/name relative flex h-full min-w-0 flex-1 items-baseline gap-1 overflow-visible text-base text-gray-900 transition-colors md:text-sm">
      <span
        ref={nameRef}
        className={cn(
          "block min-w-0 flex-1 truncate",
          isLongName && "group-hover/name:invisible",
          showExpanded && "invisible",
        )}
      >
        {name}
      </span>
      {isLongName ? (
        <span
          className={cn(
            "invisible absolute -top-1 left-0 z-50 whitespace-nowrap rounded border border-gray-200 bg-white px-2 py-1 shadow-md group-hover/name:visible",
            showExpanded && "visible",
          )}
        >
          {name}
        </span>
      ) : null}
    </div>
  );
}

function ClusterSize({ cluster }: { cluster: Cluster }) {
  const nodes = cluster.nodes || {};

  const nodeCount = Object.keys(nodes)
    // _ prefix keys are not node roles
    .filter((role) => !role.startsWith("_"))
    .reduce((sum, role) => sum + nodes[role].length, 0);

  return (
    <div
      className="flex items-center gap-1 text-sm text-muted-foreground"
      title={`${nodeCount} nodes`}
    >
      <Icon name="Server" size={14} />
      <span className="tabular-nums">{nodeCount}</span>
    </div>
  );
}
